import { Component } from "@theme/component";
import {
  ThemeEvents,
  VariantUpdateEvent,
  ZoomMediaSelectedEvent,
} from "@theme/events";

const CLOUDINARY_BASE_URL = "https://res.cloudinary.com";
const CLOUDINARY_SRCSET_WIDTHS = [480, 720, 960, 1200, 1600];
const CLOUDINARY_TRANSFORM_PREFIX = "c_fill,ar_3:4,w_";
const CLOUDINARY_TRANSFORM_SUFFIX = ",q_auto,f_auto";
const CLOUDINARY_PLACEHOLDER_TRANSFORM =
  "c_fill,ar_3:4,w_64,q_20,e_blur:600,f_auto";

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (
      this.closest('[data-purchase-context="embedded"]') &&
      this.#controller.signal.aborted
    ) {
      this.#controller = new AbortController();
    }
    const { signal } = this.#controller;
    const target = this.closest(".shopify-section, dialog");

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal },
    );
    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      {
        signal,
      },
    );

    // Preload images for hovered color swatches before the user clicks
    const variantPicker = this.#getScopedVariantPicker();
    variantPicker?.addEventListener("pointerenter", this.#handleSwatchHover, {
      signal,
      capture: true,
    });

    // Initialize simple media filtering
    this.#initializeSimpleFiltering();
    this.#initializeCloudinary();

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", this.#handleDocumentReady, {
        once: true,
        signal,
      });
    } else {
      this.#handleDocumentReady();
    }

    if (document.readyState === "complete") {
      this.#scheduleBackgroundPreload();
    } else {
      window.addEventListener("load", () => this.#scheduleBackgroundPreload(), {
        once: true,
        signal,
      });
    }
  }

  #controller = new AbortController();
  #cloudinaryImages = [];
  #mediaMetaCache = new WeakMap();
  #preloadedSrcs = new Set();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#preloadedSrcs.clear();
  }

  /**
   * Initialize simple media filtering
   */
  #initializeSimpleFiltering() {
    const isFilteringEnabled = this.dataset.enableFiltering === "true";
    if (!isFilteringEnabled) return;

    this.#buildMediaMetaCache();
    this.#applySimpleFilter();
  }

  /**
   * Pre-parse each media item's alt into a structured meta cache so the
   * variant-change hot path is set-membership lookups, not string parsing.
   * Called once on connect and again lazily if items are replaced (e.g.,
   * after Cloudinary rebuild).
   */
  #buildMediaMetaCache() {
    for (const item of this.querySelectorAll(".product-media-container")) {
      const img = item.querySelector("img");
      const altText = img?.alt?.trim() ?? "";
      const altLower = altText.toLowerCase();

      this.#mediaMetaCache.set(item, {
        hasAlt: altText.length > 0,
        hasMiniInAlt: altLower.includes("mini"),
        hasKidsInAlt: altLower.includes("kids"),
        mediaOptions: altLower
          .split(",")
          .map((option) => option.trim())
          .filter(Boolean),
      });
    }
  }

  /**
   * Apply simple media filter based on selected variant
   */
  #applySimpleFilter({ useInitialVariantFallback = false } = {}) {
    const variantChoices = this.#getCurrentVariantChoices({
      useInitialVariantFallback,
    });
    const productOptionValues = this.#getProductOptionValues({
      useInitialVariantFallback,
    });

    // If we can't determine variant choices yet (e.g., variant picker not ready),
    // show all images to avoid incorrectly hiding them on page load
    if (variantChoices.length === 0 || productOptionValues.length === 0) {
      return false;
    }

    const mediaItems = this.querySelectorAll(".product-media-container");

    // Self-heal: if items aren't in the meta cache yet (e.g., Cloudinary
    // rebuilt the gallery with fresh DOM nodes), rebuild before iterating.
    if (mediaItems.length > 0 && !this.#mediaMetaCache.has(mediaItems[0])) {
      this.#buildMediaMetaCache();
    }

    const choicesSet = new Set(
      variantChoices.map((choice) => choice.toLowerCase()),
    );
    const optionsSet = new Set(
      productOptionValues.map((value) => value.toLowerCase()),
    );
    const ctx = {
      choicesSet,
      optionsSet,
      testFilter: this.dataset.testFilter === "true",
      productHasMiniOption: optionsSet.has("mini"),
      productHasKidsOption: optionsSet.has("kids"),
      hasMiniSelected: choicesSet.has("mini"),
      hasKidsSelected: choicesSet.has("kids"),
    };

    for (const item of mediaItems) {
      item.classList.toggle(
        "media-filtered-hidden",
        !this.#shouldShowMedia(item, ctx),
      );
    }

    // Mirror the slide filtering onto the thumbnail strip.
    this.#syncFilteredThumbnails();

    // Trigger visibility check on newly visible videos
    this.#triggerDeferredMediaCheck();

    return true;
  }

  /**
   * Hide thumbnails whose corresponding slide was filtered out by the variant.
   *
   * Thumbnails are generated 1:1 with the slides (same `sorted_media` order),
   * but #applySimpleFilter only toggles `.media-filtered-hidden` on the slide
   * containers. Without this sync the thumbnail strip keeps showing every
   * colour's image even though the slideshow itself is filtered to the selected
   * variant. Index alignment holds because both lists come from the same
   * server-rendered order.
   */
  #syncFilteredThumbnails() {
    const slideshow = this.querySelector("slideshow-component");
    if (!slideshow) return;

    // Slide containers within the slideshow, in order. Grid and zoom-dialog
    // media live outside <slideshow-component>, so they're excluded here.
    const slideHiddenStates = Array.from(
      slideshow.querySelectorAll(".product-media-container"),
      (container) => container.classList.contains("media-filtered-hidden"),
    );
    if (slideHiddenStates.length === 0) return;

    // A template may render more than one thumbnail strip (e.g. separate
    // desktop and mobile controls), so sync each strip independently by index.
    for (const strip of slideshow.querySelectorAll(
      ".slideshow-controls__thumbnails",
    )) {
      strip
        .querySelectorAll(".slideshow-controls__thumbnail")
        .forEach((thumbnail, index) => {
          if (index >= slideHiddenStates.length) return;
          thumbnail.classList.toggle(
            "media-filtered-hidden",
            slideHiddenStates[index],
          );
        });
    }
  }

  #handleDocumentReady = () => {};

  /**
   * Trigger visibility re-check on deferred-media elements that are now visible.
   * This is called after variant filtering to load videos that became visible.
   */
  #triggerDeferredMediaCheck() {
    const visibleMedia = this.querySelectorAll(
      ".product-media-container:not(.media-filtered-hidden) deferred-media[autoplay]:not([data-media-loaded])",
    );

    for (const media of visibleMedia) {
      // checkVisibility is a public method on DeferredMedia (see assets/media.js)
      if (typeof media.checkVisibility === "function") {
        media.checkVisibility();
      }
    }
  }

  #getActiveContainer() {
    if (this.presentation !== "grid") {
      return this.querySelector("slideshow-component");
    }
    const isDesktop = window.matchMedia("(min-width: 750px)").matches;
    return isDesktop
      ? this.querySelector(".media-gallery__grid")
      : this.querySelector("slideshow-component");
  }

  #isCloudinaryEnabled() {
    return Boolean(this.dataset.cloudinaryHandle && this.dataset.cloudName);
  }

  async #initializeCloudinary() {
    if (!this.#isCloudinaryEnabled()) return;

    try {
      await this.#fetchCloudinaryImages();
      if (!this.isConnected) return;
      this.#renderCloudinaryGallery();
    } catch {
      // Cloudinary fetch failed — Shopify images remain as fallback
    }
  }

  async #fetchCloudinaryImages() {
    const { cloudinaryHandle, cloudName } = this.dataset;
    const url = `${CLOUDINARY_BASE_URL}/${cloudName}/image/list/${cloudinaryHandle}.json`;
    const response = await fetch(url, { signal: this.#controller.signal });
    if (!response.ok) throw new Error(`${response.status}`);

    const data = await response.json();
    this.#cloudinaryImages = (data.resources || [])
      .map((resource) => ({
        publicId: resource.public_id,
        format: resource.format,
        width: resource.width,
        height: resource.height,
        variantOptions: resource.context?.custom?.variant_options || "",
        modelName: resource.context?.custom?.model_name || "",
        modelHeight: resource.context?.custom?.model_height || "",
        order: parseInt(resource.context?.custom?.order || "0", 10),
      }))
      .sort((a, b) => a.order - b.order);
  }

  #buildCloudinaryImageUrl(publicId, width) {
    const { cloudName } = this.dataset;
    return `${CLOUDINARY_BASE_URL}/${cloudName}/image/upload/${CLOUDINARY_TRANSFORM_PREFIX}${width}${CLOUDINARY_TRANSFORM_SUFFIX}/${publicId}`;
  }

  #buildCloudinaryPlaceholderUrl(publicId) {
    const { cloudName } = this.dataset;
    return `${CLOUDINARY_BASE_URL}/${cloudName}/image/upload/${CLOUDINARY_PLACEHOLDER_TRANSFORM}/${publicId}`;
  }

  #buildCloudinarySrcset(publicId) {
    return CLOUDINARY_SRCSET_WIDTHS.map(
      (w) => `${this.#buildCloudinaryImageUrl(publicId, w)} ${w}w`,
    ).join(", ");
  }

  #renderCloudinaryGallery() {
    if (this.#cloudinaryImages.length === 0) return;

    const gridContainer = this.querySelector(".media-gallery__grid");
    if (gridContainer) {
      this.#replaceGridMedia(gridContainer);
    }

    // Replace slideshow slides with Cloudinary images
    this.#updateSlideshowMedia();

    // Re-apply variant filtering to the new Cloudinary images
    if (this.dataset.enableFiltering === "true") {
      this.#applySimpleFilter({ useInitialVariantFallback: true });
      this.#selectFirstVisibleSlideshowSlide({ defer: true });
    }

    this.#ensureVisibleImagesLoad();
  }

  /**
   * After variant filtering, the first image (loading="eager") may be hidden,
   * leaving only lazy images visible. Safari iOS won't trigger lazy loading for
   * images inside content-visibility:hidden slideshow slides, so switch all
   * visible images to eager loading.
   */
  #ensureVisibleImagesLoad() {
    const container = this.#getActiveContainer();
    if (!container) return;

    const visibleImages = container.querySelectorAll(
      ".product-media-container:not(.media-filtered-hidden) .product-media__image",
    );

    for (const [index, image] of visibleImages.entries()) {
      if (!(image instanceof HTMLImageElement)) continue;

      image.loading = "eager";
      const priority = index === 0 ? "high" : "auto";
      image.setAttribute("fetchpriority", priority);
      if ("fetchPriority" in image) {
        image.fetchPriority = priority;
      }
    }
  }

  #replaceGridMedia(gridContainer) {
    const containerClass =
      this.dataset.mediaContainerClass ||
      "product-media-container product-media-container--image";
    const needsConstraintWrapper = containerClass.includes("constrain-height");
    gridContainer.innerHTML = "";

    for (const [index, image] of this.#cloudinaryImages.entries()) {
      const li = document.createElement("li");
      li.setAttribute("ref", "media[]");
      li.className = containerClass;

      const mediaDiv = this.#createCloudinaryMediaDiv(image, index);
      if (needsConstraintWrapper) {
        const constraint = document.createElement("div");
        constraint.className = "product-media-constraint-wrapper";
        constraint.appendChild(mediaDiv);
        li.appendChild(constraint);
      } else {
        li.appendChild(mediaDiv);
      }
      gridContainer.appendChild(li);
    }
  }

  #updateSlideshowMedia() {
    const slideshowComponent = this.querySelector("slideshow-component");
    if (!slideshowComponent) return;

    const scroller = slideshowComponent.querySelector("[ref='scroller']");
    if (!scroller) return;

    const existingSlides = scroller.querySelectorAll("slideshow-slide");
    if (existingSlides.length === 0) return;

    // Use first slide as a structural template, then clear all slides
    const templateSlide = existingSlides[0];
    for (const slide of existingSlides) {
      slide.remove();
    }

    // Build fresh slides from Cloudinary data
    const containerClass =
      this.dataset.mediaContainerClass ||
      "product-media-container product-media-container--image";

    for (const [index, image] of this.#cloudinaryImages.entries()) {
      const slide = templateSlide.cloneNode(false);
      slide.setAttribute("ref", "slides[]");
      slide.setAttribute("aria-hidden", index === 0 ? "false" : "true");
      slide.removeAttribute("hidden");
      slide.style.viewTimelineName = `--slide-${index}`;

      const container = document.createElement("div");
      container.className = containerClass;
      container.appendChild(this.#createCloudinaryMediaDiv(image, index));
      slide.appendChild(container);

      scroller.appendChild(slide);
    }

    this.#updateSlideshowControls(slideshowComponent);
  }

  #updateSlideshowControls(slideshowComponent) {
    const totalImages = this.#cloudinaryImages.length;

    // Update all dot control sets (desktop + mobile may render separately)
    for (const dotsList of slideshowComponent.querySelectorAll(
      ".slideshow-controls__dots",
    )) {
      const existingDots = dotsList.querySelectorAll("li");

      // Remove excess dots
      for (let i = existingDots.length - 1; i >= totalImages; i -= 1) {
        existingDots[i].remove();
      }

      // Add missing dots
      for (let i = existingDots.length; i < totalImages; i += 1) {
        const li = document.createElement("li");
        const btn = document.createElement("button");
        btn.className = "slideshow-control button button-unstyled";
        btn.style.animationTimeline = `--slide-${i + 1}`;
        btn.setAttribute("on:click", `/select/${i}`);
        btn.setAttribute("ref", "dots[]");
        btn.textContent = i + 1;
        li.appendChild(btn);
        dotsList.appendChild(li);
      }
    }

    // Update all counter totals
    for (const counter of slideshowComponent.querySelectorAll(
      ".slideshow-controls__counter",
    )) {
      const slash = counter.querySelector(".slash");
      if (slash && slash.nextSibling) {
        slash.nextSibling.textContent = totalImages;
      }
    }
  }

  #createCloudinaryMediaDiv(image, index) {
    const src = this.#buildCloudinaryImageUrl(image.publicId, 1200);
    const srcset = this.#buildCloudinarySrcset(image.publicId);
    const placeholderUrl = this.#buildCloudinaryPlaceholderUrl(image.publicId);

    const wrapper = document.createElement("div");
    wrapper.className = "product-media product-media--cloudinary-placeholder";
    wrapper.style.setProperty("--ratio", "0.75");
    wrapper.style.setProperty(
      "--cloudinary-placeholder-url",
      `url('${placeholderUrl}')`,
    );

    const img = document.createElement("img");
    img.className = "product-media__image";
    img.src = src;
    img.srcset = srcset;
    img.alt = image.variantOptions;
    img.decoding = "async";
    img.loading = index === 0 ? "eager" : "lazy";
    img.sizes = "(min-width: 750px) 50vw, 100vw";
    if ("fetchPriority" in img) {
      img.fetchPriority = index === 0 ? "high" : "auto";
    }
    img.width = 1200;
    img.height = 1600;

    wrapper.appendChild(img);
    this.#addMetadataOverlay(wrapper, image);
    this.#bindCloudinaryLoadState(img, wrapper);
    return wrapper;
  }

  #bindCloudinaryLoadState(img, wrapper) {
    const { signal } = this.#controller;
    wrapper.classList.add("product-media--cloudinary-loading");

    const markLoaded = () => {
      wrapper.classList.remove("product-media--cloudinary-loading");
      wrapper.classList.add("product-media--cloudinary-loaded");
    };

    img.addEventListener("load", markLoaded, { once: true, signal });
    img.addEventListener("error", markLoaded, { once: true, signal });

    // Handle already-cached images
    if (img.complete && img.naturalWidth > 0) {
      markLoaded();
    }
  }

  #addMetadataOverlay(container, image) {
    if (!image.modelName && !image.modelHeight) return;

    const overlay = document.createElement("div");
    overlay.className = "cloudinary-media__overlay";
    const parts = [image.modelName, image.modelHeight].filter(Boolean);
    overlay.textContent = parts.join(", ");
    container.querySelector(".product-media")?.appendChild(overlay) ??
      container.appendChild(overlay);
  }

  #getFirstVisibleSlideshowIndex() {
    const slideshowSlides = this.refs.slideshow?.slides;
    if (!slideshowSlides?.length) {
      return undefined;
    }

    const firstVisibleIndex = slideshowSlides.findIndex(
      (slide) => !slide.classList.contains("media-filtered-hidden"),
    );

    if (firstVisibleIndex >= 0) {
      return firstVisibleIndex;
    }

    return 0;
  }

  #selectFirstVisibleSlideshowSlide({ defer = false } = {}) {
    const firstVisibleSlideIndex = this.#getFirstVisibleSlideshowIndex();
    if (typeof firstVisibleSlideIndex !== "number" || !this.refs.slideshow) {
      return;
    }

    const selectSlide = () => {
      if (!this.isConnected || !this.refs.slideshow) return;

      this.refs.slideshow.select(firstVisibleSlideIndex, undefined, {
        animate: false,
        force: true,
      });
    };

    if (defer) {
      window.requestAnimationFrame(selectSlide);
      return;
    }

    selectSlide();
  }

  #getMediaContainers(container = this) {
    return Array.from(container.querySelectorAll(".product-media-container"));
  }

  /**
   * Get current variant choices (like the Liquid variant_option_choices)
   */
  #getCurrentVariantChoices({ useInitialVariantFallback = false } = {}) {
    const variantPicker = this.#getScopedVariantPicker();
    const choices = [];

    if (variantPicker) {
      // Get from select elements
      variantPicker
        .querySelectorAll("select:not([data-auxiliary-select])")
        .forEach((select) => {
          if (select.selectedIndex >= 0) {
            const option = select.options[select.selectedIndex];
            if (option && option.value) {
              choices.push(option.value.trim());
            }
          }
        });

      // Get from checked radio buttons
      variantPicker
        .querySelectorAll('input[type="radio"]:checked')
        .forEach((input) => {
          if (input.value) {
            choices.push(input.value.trim());
          }
        });
    }

    if (choices.length > 0 || !useInitialVariantFallback) {
      return choices;
    }

    return this.#getInitialVariantChoices();
  }

  /**
   * Get all product option values (like Liquid product.options_with_values)
   */
  #getProductOptionValues({ useInitialVariantFallback = false } = {}) {
    const variantPicker = this.#getScopedVariantPicker();
    const allValues = new Set();

    if (variantPicker) {
      // Get all option values from select elements
      variantPicker.querySelectorAll("select option").forEach((option) => {
        if (option.value && option.value !== "") {
          allValues.add(option.value.trim());
        }
      });

      // Get all option values from radio buttons
      variantPicker.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (input.value && input.value !== "") {
          allValues.add(input.value.trim());
        }
      });
    }

    const values = Array.from(allValues);
    if (values.length > 0 || !useInitialVariantFallback) {
      return values;
    }

    return this.#getInitialProductOptionValues();
  }

  #getScopedTarget() {
    return this.closest(".shopify-section, dialog");
  }

  #getScopedVariantPicker() {
    return this.#getScopedTarget()?.querySelector("variant-picker") || null;
  }

  #getInitialVariantChoices() {
    return this.#parseDatasetArray("initialVariantOptions");
  }

  #getInitialProductOptionValues() {
    return this.#parseDatasetArray("productOptionValues");
  }

  #parseDatasetArray(key) {
    const rawValue = this.dataset[key];
    if (!rawValue) {
      return [];
    }

    try {
      const parsedValue = JSON.parse(rawValue);
      if (!Array.isArray(parsedValue)) {
        return [];
      }

      return parsedValue
        .map((value) =>
          typeof value === "string" ? value.trim() : `${value}`.trim(),
        )
        .filter(Boolean);
    } catch {
      return [];
    }
  }

  /**
   * Determine if media should be shown based on alt tag (replicating Liquid logic).
   * Uses precomputed per-item meta + per-call Sets so all comparisons are O(1).
   */
  #shouldShowMedia(mediaItem, ctx) {
    const meta = this.#mediaMetaCache.get(mediaItem);
    // No cached meta or no alt tag → show for all variants (matches Liquid)
    if (!meta || !meta.hasAlt) return true;

    const { hasMiniInAlt, hasKidsInAlt, mediaOptions } = meta;
    const {
      choicesSet,
      optionsSet,
      testFilter,
      productHasMiniOption,
      productHasKidsOption,
      hasMiniSelected,
      hasKidsSelected,
    } = ctx;

    // EDGE CASE: Mini/Kids product filtering — only runs on the combined eden-hoodie product
    // where "Mini" and "Kids" are actual variant option values. Standalone eden-hoodie-kids/mini
    // products skip this block entirely so their images are never incorrectly hidden.
    if (productHasMiniOption || productHasKidsOption) {
      // Mini images show ONLY when "Mini" is selected
      if (hasMiniInAlt && productHasMiniOption && !hasMiniSelected) {
        return false;
      }

      // Kids images show ONLY when "Kids" is selected
      if (hasKidsInAlt && productHasKidsOption && !hasKidsSelected) {
        return false;
      }

      // Standard images hide when EITHER "Mini" OR "Kids" is selected
      if (
        !hasMiniInAlt &&
        !hasKidsInAlt &&
        (hasMiniSelected || hasKidsSelected)
      ) {
        return false;
      }
    }

    // Check if any media option matches any product option value (like Liquid).
    // mediaOptions are pre-lowercased; the TEST sentinel is dropped at runtime
    // when TEST images are promoted by the gallery setting.
    let hasProductOptionMatch = false;
    for (const option of mediaOptions) {
      if (testFilter && option === "test") continue;
      if (optionsSet.has(option)) {
        hasProductOptionMatch = true;
        break;
      }
    }

    // No product option matches (e.g., "4 ITEM BUNDLE") → show for all variants
    if (!hasProductOptionMatch) return true;

    // ALL media options must be present in variant choices
    for (const option of mediaOptions) {
      if (testFilter && option === "test") continue;
      if (!choicesSet.has(option)) return false;
    }
    return true;
  }

  #scheduleBackgroundPreload() {
    const conn = /** @type {any} */ (navigator).connection;
    if (conn?.saveData) return;
    if (conn?.effectiveType === "slow-2g" || conn?.effectiveType === "2g")
      return;

    const schedule = window.requestIdleCallback
      ? (cb) => requestIdleCallback(cb, { timeout: 5000 })
      : (cb) => setTimeout(cb, 1000);

    schedule(() => {
      for (const item of this.querySelectorAll(".product-media-container")) {
        const img = item.querySelector("img.product-media__image");
        if (!img?.src || this.#preloadedSrcs.has(img.src)) continue;
        this.#preloadedSrcs.add(img.src);
        const preload = new Image();
        preload.src = img.src;
        if (img.srcset) {
          preload.srcset = img.srcset;
          preload.sizes = img.sizes;
        }
      }
    });
  }

  #handleSwatchHover = (event) => {
    if (
      !(event.target instanceof HTMLInputElement) ||
      event.target.type !== "radio"
    )
      return;

    const hoverValue = event.target.value.toLowerCase();
    let count = 0;

    for (const item of this.querySelectorAll(".product-media-container")) {
      if (count >= 4) break;
      const meta = this.#mediaMetaCache.get(item);
      if (!meta?.mediaOptions.includes(hoverValue)) continue;
      const img = item.querySelector("img.product-media__image");
      if (!img?.src || this.#preloadedSrcs.has(img.src)) continue;
      this.#preloadedSrcs.add(img.src);
      const preload = new Image();
      preload.src = img.src;
      if (img.srcset) {
        preload.srcset = img.srcset;
        preload.sizes = img.sizes;
      }
      count++;
    }
  };

  /**
   * Handles a variant update event by filtering media or replacing the gallery
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    if (
      this.closest('[data-purchase-context="embedded"]') &&
      event.detail.data.html
    ) {
      const gallery = event.detail.data.html.querySelector(
        ".product-information media-gallery",
      );
      if (gallery) this.replaceWith(gallery);
      return;
    }
    // Check if this is a combined listing (different product loaded)
    const isCombinedListing = event.detail.data.newProduct != null;

    // If filtering is enabled, filter instead of replacing. setTimeout(0)
    // schedules the per-image loop as a separate task so the browser can
    // paint the variant-update changes (button morph, checked state) before
    // it runs — avoids requestAnimationFrame, which is throttled or paused
    // on backgrounded tabs and would leave the filter stale until refocus.
    if (this.dataset.enableFiltering === "true") {
      setTimeout(() => {
        if (!this.isConnected) return;
        this.#applySimpleFilter();

        // Reset slideshow to the first visible image (unless combined listing)
        if (!isCombinedListing && this.refs.slideshow) {
          this.#selectFirstVisibleSlideshowSlide();
        }
      }, 0);
      return;
    }

    // Default behavior - replace gallery
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector("media-gallery");

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
    // Note: When gallery is replaced, the new gallery's slideshow starts at its initial position
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get("media-gallery")) {
  customElements.define("media-gallery", MediaGallery);
}
